import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ChevronLeft,
    CheckCircle2,
    Wallet,
    ShieldCheck,
    Users,
    Clock,
    PlaneTakeoff,
    Route,
    Banknote,
    Car,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { formatCurrency } from "../lib/format";
import PhoneInputField from "../components/PhoneInputField";
import SEO from "../components/SEO";

const Steps = ["Transfer details", "Traveller info", "Confirm & Pay"];

const VEHICLES = [
    { id: "sedan", label: "Sedan", capacity: "1–4 pax", icon: Car },
    { id: "suv", label: "SUV", capacity: "5–7 pax", icon: Car },
    { id: "tempo", label: "Tempo / Van", capacity: "8–14 pax", icon: Car },
];

const fmtDuration = (min) => {
    if (!min) return "—";
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} h`;
    return `${h} h ${m}`;
};

const TransferBooking = () => {
    const { transferId } = useParams();
    const navigate = useNavigate();
    const { transfers, createBooking, settings } = useData();
    const { user } = useAuth();

    const transfer = transfers.find((t) => t.id === transferId);

    const [step, setStep] = useState(0);
    const [vehicleClass, setVehicleClass] = useState("sedan");
    const [pickupDateTime, setPickupDateTime] = useState("");
    const [passengers, setPassengers] = useState(2);
    const [flightNumber, setFlightNumber] = useState("");

    const [name, setName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [phone, setPhone] = useState(user?.phone || "");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const priceForClass = (c) =>
        c === "sedan"
            ? transfer?.sedanPrice
            : c === "suv"
            ? transfer?.suvPrice
            : transfer?.tempoPrice;

    const totalAmount = useMemo(
        () => priceForClass(vehicleClass) || 0,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [transfer, vehicleClass],
    );

    if (!transfer) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="text-center">
                    <p className="text-ink-light">Transfer not found.</p>
                    <button onClick={() => navigate("/airport-transfers")} className="mt-4 text-blue-500 font-semibold">Back to transfers</button>
                </div>
            </div>
        );
    }

    if (settings && settings.bookingsEnabled === false) {
        return (
            <div className="min-h-[60vh] max-w-md mx-auto px-4 py-20 text-center">
                <h1 className="font-display text-3xl text-ink">Bookings paused</h1>
                <p className="text-ink-light mt-2">Online bookings are temporarily disabled.</p>
            </div>
        );
    }

    const next = () => setStep((s) => Math.min(2, s + 1));
    const prev = () => setStep((s) => Math.max(0, s - 1));

    const submit0 = (e) => {
        e.preventDefault();
        if (!pickupDateTime) return toast.error("Please choose a pickup date & time.");
        if (!priceForClass(vehicleClass))
            return toast.error("Selected vehicle class is not available for this transfer.");
        next();
    };
    const submit1 = (e) => {
        e.preventDefault();
        if (!name || !email || !phone) return toast.error("Please complete all details.");
        if (!/\S+@\S+\.\S+/.test(email)) return toast.error("Enter a valid email.");
        if (phone.replace(/\D/g, "").length < 10) return toast.error("Enter a valid phone number.");
        next();
    };
    const handleConfirm = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const created = await createBooking({
                type: "transfer",
                transferId: transfer.id,
                vehicleClass,
                pickupDateTime,
                flightNumber: flightNumber || undefined,
                passengers,
                customer: { name, email, phone, notes },
            });
            toast.success("Transfer booked! Confirmation email sent.");
            navigate(`/booking/confirmation/${created.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create booking.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-cream-100 min-h-screen pb-20" data-testid="transfer-booking-page">
            <SEO
                title={`${transfer.fromLocation} to ${transfer.toLocation} Taxi · Fixed Price Transfer · SouthTrip.in`}
                description={`Private taxi from ${transfer.fromLocation} to ${transfer.toLocation} (${transfer.distanceKm} km). Fixed price, verified driver, no hidden charges. Book direct with SouthTrip.in.`}
                path={`/airport-transfers/book/${transfer.id}`}
            />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                <button onClick={() => navigate(-1)} className="text-sm text-ink-light hover:text-ink flex items-center gap-1 mb-4">
                    <ChevronLeft className="w-4 h-4" /> Back
                </button>

                <div className="flex items-center justify-center gap-2 md:gap-4 mb-8">
                    {Steps.map((s, i) => (
                        <React.Fragment key={s}>
                            <div className={`flex items-center gap-2 ${i === step ? "text-blue-500" : i < step ? "text-blue-600" : "text-ink-light/60"}`}>
                                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                    i === step ? "bg-blue-500 text-white" : i < step ? "bg-blue-600 text-white" : "bg-white border border-cream-200"
                                }`}>
                                    {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                                </span>
                                <span className="hidden md:inline text-sm font-semibold">{s}</span>
                            </div>
                            {i < Steps.length - 1 && (
                                <span className={`w-8 md:w-16 h-0.5 ${i < step ? "bg-blue-600" : "bg-cream-200"}`} />
                            )}
                        </React.Fragment>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-8 space-y-6">
                        {step === 0 && (
                            <form onSubmit={submit0} className="bg-white rounded-2xl border border-cream-200 p-6">
                                <h2 className="font-display text-2xl text-ink font-bold mb-4">Transfer details</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Vehicle class</label>
                                        <div className="grid grid-cols-3 gap-2 mt-2">
                                            {VEHICLES.map((v) => {
                                                const price = priceForClass(v.id);
                                                const disabled = !price;
                                                const active = vehicleClass === v.id;
                                                return (
                                                    <button
                                                        key={v.id}
                                                        type="button"
                                                        disabled={disabled}
                                                        onClick={() => setVehicleClass(v.id)}
                                                        data-testid={`tb-vehicle-${v.id}`}
                                                        className={`p-3 rounded-xl border-2 text-left transition ${
                                                            disabled
                                                                ? "border-cream-200 opacity-40 cursor-not-allowed"
                                                                : active
                                                                ? "border-blue-500 bg-blue-50"
                                                                : "border-cream-200 hover:border-blue-300"
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <v.icon className="w-5 h-5 text-blue-500" />
                                                            {active && <CheckCircle2 className="w-4 h-4 text-blue-500" />}
                                                        </div>
                                                        <div className="mt-2 font-semibold text-ink text-sm">{v.label}</div>
                                                        <div className="text-[10px] text-ink-softer uppercase tracking-wider mt-0.5">{v.capacity}</div>
                                                        <div className="mt-2 font-display text-xl font-bold text-blue-500">
                                                            {price ? formatCurrency(price) : "—"}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Pickup date &amp; time</label>
                                        <input type="datetime-local" min={new Date().toISOString().slice(0, 16)} value={pickupDateTime} onChange={(e) => setPickupDateTime(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="tb-pickup-datetime" required />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Passengers</label>
                                        <input type="number" min={1} max={14} value={passengers} onChange={(e) => setPassengers(Number(e.target.value))} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="tb-passengers" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">
                                            Flight number <span className="text-ink-softer normal-case">(optional · for airport transfers)</span>
                                        </label>
                                        <input value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} placeholder="e.g. 6E-5234" className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="tb-flight-number" />
                                    </div>
                                </div>
                                <div className="flex justify-end mt-5">
                                    <button type="submit" className="px-7 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition shadow-sm" data-testid="tb-step0-next">Continue</button>
                                </div>
                            </form>
                        )}

                        {step === 1 && (
                            <form onSubmit={submit1} className="bg-white rounded-2xl border border-cream-200 p-6">
                                <h2 className="font-display text-2xl text-ink font-bold mb-4">Lead traveller</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Full name</label>
                                        <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="tb-name" required />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Email</label>
                                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="tb-email" required />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Phone</label>
                                        <PhoneInputField value={phone} onChange={setPhone} testId="tb-phone" required />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Notes (optional)</label>
                                        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any special requirements (child seat, luggage, etc.)" className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" />
                                    </div>
                                </div>
                                <div className="flex justify-between mt-5">
                                    <button type="button" onClick={prev} className="px-5 py-3 rounded-xl border border-cream-200 text-ink font-semibold">Back</button>
                                    <button type="submit" className="px-7 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition shadow-sm" data-testid="tb-step1-next">Continue</button>
                                </div>
                            </form>
                        )}

                        {step === 2 && (
                            <form onSubmit={handleConfirm} className="bg-white rounded-2xl border border-cream-200 p-6">
                                <h2 className="font-display text-2xl text-ink font-bold mb-2">Confirm your transfer</h2>
                                <p className="text-sm text-ink-light mb-4">No prepayment required — pay the driver on arrival.</p>

                                <div className="rounded-xl border-2 border-blue-500 bg-blue-50 p-5 flex items-center gap-4" data-testid="tb-pay-direct-driver">
                                    <span className="w-12 h-12 rounded-xl bg-blue-500 text-white flex items-center justify-center shrink-0">
                                        <Wallet className="w-6 h-6" />
                                    </span>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-ink">Direct to Driver</span>
                                            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500 text-white font-semibold">On arrival</span>
                                        </div>
                                        <div className="text-sm text-ink-light mt-1">Cash or UPI directly to your driver. Receipt on demand.</div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 mt-4 px-4 py-3 rounded-lg bg-cream-100 text-ink-light text-xs border border-cream-200">
                                    <ShieldCheck className="w-4 h-4 text-blue-500 shrink-0" />
                                    <span>Your transfer is held without prepayment. We'll confirm by WhatsApp and the driver will reach your pickup spot 10 min early.</span>
                                </div>

                                <div className="flex justify-between mt-5">
                                    <button type="button" onClick={prev} className="px-5 py-3 rounded-xl border border-cream-200 text-ink font-semibold">Back</button>
                                    <button type="submit" disabled={submitting} className="px-8 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition shadow-md brand-shadow disabled:opacity-60 flex items-center gap-2" data-testid="tb-confirm-pay">
                                        <Banknote className="w-4 h-4" />
                                        {submitting ? "Booking…" : `Confirm · ${formatCurrency(totalAmount)}`}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    <aside className="lg:col-span-4">
                        <div className="bg-white rounded-2xl border border-cream-200 overflow-hidden sticky top-24">
                            <img src={transfer.image} alt={transfer.title} className="w-full h-40 object-cover" />
                            <div className="p-5">
                                <div className="text-xs font-semibold uppercase tracking-wider text-blue-500 flex items-center gap-1">
                                    {transfer.category === "airport-hotel" ? <PlaneTakeoff className="w-3 h-3" /> : <Route className="w-3 h-3" />}
                                    {transfer.category === "airport-hotel" ? "Airport transfer" : "Inter-city"}
                                </div>
                                <h3 className="font-display text-xl text-ink font-bold leading-tight mt-1">{transfer.title}</h3>
                                <div className="text-xs text-ink-light flex items-center gap-3 mt-1">
                                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDuration(transfer.durationMin)}</span>
                                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {passengers} pax</span>
                                </div>
                                <dl className="mt-4 space-y-2 text-sm pt-4 border-t border-cream-200">
                                    <div className="flex justify-between"><dt className="text-ink-light">Vehicle class</dt><dd className="font-medium text-ink capitalize">{vehicleClass}</dd></div>
                                    <div className="flex justify-between"><dt className="text-ink-light">Pickup</dt><dd className="font-medium text-ink">{pickupDateTime ? new Date(pickupDateTime).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</dd></div>
                                    {flightNumber && <div className="flex justify-between"><dt className="text-ink-light">Flight</dt><dd className="font-medium text-ink">{flightNumber}</dd></div>}
                                </dl>
                                <div className="mt-4 pt-4 border-t border-cream-200 flex items-center justify-between">
                                    <span className="text-ink-light text-sm">Total</span>
                                    <span className="font-display text-2xl font-bold text-blue-500" data-testid="tb-total">{formatCurrency(totalAmount)}</span>
                                </div>
                                <div className="text-[11px] text-ink-light mt-1 text-right">Payable to driver on arrival</div>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default TransferBooking;
